import { ChangeDetectorRef, Component } from '@angular/core';

import type { Observable } from 'rxjs';
import { Subject, take } from 'rxjs';

import type { User } from '@recruitee/account-types';
import type { Attachment } from '@recruitee/attachments-types';
import { filterBoolean } from '@recruitee/common';
import type { ApprovalRequestPayload } from '@recruitee/requisitions-data';
import type {
  ApprovalRequestData,
  RequisitionFlow,
  RequisitionFlowStep,
} from '@recruitee/requisitions-types';
import type { DialogComponentI } from '@recruitee/user-interface';
import { DialogRef } from '@recruitee/user-interface';
import { generateUuid } from '@recruitee/utils';

import { RequisitionsDataProviderService } from '../../data/single-requisition/requisitions-data-provider.service';
import { ApprovalRequestFlowPreviewComponent } from './approval-request-flow-preview/approval-request-flow-preview.component';
import { ApprovalRequestMessageComponent } from './approval-request-message/approval-request-message.component';

enum Step {
  Flow = 'flow',
  Message = 'message',
}

export type ApprovalRequestDialogData = {
  departmentName: string | undefined;
  isDepartmentsMode: boolean;
  approvalFlow: RequisitionFlow;
  adminsMap: Record<number, User>;
  approvalRequestData?: ApprovalRequestData | null;
  submitHandler: (data: ApprovalRequestPayload) => Observable<boolean>;
  deleteAttachmentHandler$: (attachmentId: number) => Observable<boolean>;
  uploadAttachmentHandler$: (file: File, uuid: string) => Observable<Attachment>;
};

@Component({
  selector: 'rt-requisitions-approval-request-dialog',
  templateUrl: './approval-request-dialog.component.html',
  styleUrls: ['./approval-request-dialog.component.less'],
  imports: [ApprovalRequestFlowPreviewComponent, ApprovalRequestMessageComponent],
  providers: [RequisitionsDataProviderService],
})
export class ApprovalRequestDialogComponent
  implements DialogComponentI<ApprovalRequestDialogData, void>
{
  public readonly Step = Step;

  public step: Step = Step.Flow;
  public submitting = false;

  public flow: RequisitionFlow;
  public adminsMap: Record<number, User>;
  public approvers: User[];
  public attachments: Attachment[] = [];
  public attachmentsUploading: string[] = [];
  public message: object | null;
  public departmentName: string | undefined;
  public isDepartmentsMode: boolean;

  public submitHandler: (data: ApprovalRequestPayload) => Observable<boolean>;
  public deleteAttachmentHandler$: (attachmentId: number) => Observable<boolean>;
  public uploadAttachmentHandler$: (file: File, uuid: string) => Observable<Attachment>;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private dialogRef: DialogRef,
    private cdRef: ChangeDetectorRef,
  ) {}

  public changeStep(step: Step) {
    this.step = step;
    this.cdRef.detectChanges();
  }

  public submit(data: ApprovalRequestPayload) {
    this.submitting = true;
    this.cdRef.detectChanges();
    this.submitHandler(data)
      .pipe(take(1), filterBoolean)
      .subscribe(() => this.dialogRef.close());
  }

  public cancel() {
    this.dialogRef.close();
  }

  public setDialogData(data: ApprovalRequestDialogData, triggerUpdate: boolean): void {
    this.departmentName = data.departmentName;
    this.isDepartmentsMode = data.isDepartmentsMode;
    this.flow = data.approvalFlow;
    this.adminsMap = data.adminsMap;
    this.message = data.approvalRequestData?.bodyJson ?? null;
    this.attachments = data.approvalRequestData?.attachments ?? [];
    this.submitHandler = data.submitHandler;
    this.deleteAttachmentHandler$ = data.deleteAttachmentHandler$;
    this.uploadAttachmentHandler$ = data.uploadAttachmentHandler$;
    this.approvers = this.flatStepApproversToUsers(data.approvalFlow.steps);
  }

  public uploadAttachment(file: File): void {
    const uuid = generateUuid();
    this.attachmentsUploading.push(uuid);
    this.cdRef.detectChanges();

    this.uploadAttachmentHandler$(file, uuid)
      .pipe(take(1))
      .subscribe(attachment => {
        this.attachments = [...this.attachments, attachment];
        this.attachmentsUploading = this.attachmentsUploading.filter(
          uploadingUuid => uploadingUuid !== uuid,
        );
        this.cdRef.detectChanges();
      });
  }

  public deleteAttachment(attachmentId: number): void {
    this.deleteAttachmentHandler$(attachmentId)
      .pipe(take(1))
      .subscribe(() => {
        this.attachments = this.attachments.filter(attachment => attachment.id !== attachmentId);
        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private flatStepApproversToUsers(steps: RequisitionFlowStep[]): User[] {
    return [...new Set(steps.map(step => step.approverIds).flat())]
      .filter(id => !!this.adminsMap[id])
      .map(id => this.adminsMap[id]!);
  }
}
